import React from "react";
import {
  Popover,
  Box,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";

const CHANNEL_PACKAGES = {
  sms: {
    label: "SMS",
    price: "R$ 80,00",
    unit: "1.000 unidades",
    info: "Cobrança a cada pacote de mil (1000/un), solicitados antecipadamente ao suporte.",
  },
  email: {
    label: "E-mail",
    price: "R$ 10,00",
    unit: "1.000 unidades",
    info: "Cobrança a cada pacote de mil (1000/un), solicitados antecipadamente ao suporte.",
  },
  whatsapp_web: {
    label: "WhatsApp Web",
    price: "Grátis",
    unit: "Ilimitado",
    info: "Canal sem custo oficial de API da Meta.",
  },
};

const WHATSAPP_PRICING = [
  {
    range: "0 - 10k",
    service: "Grátis",
    marketing: "R$ 0,375",
    utility: "R$ 0,040",
    authentication: "R$ 0,040",
    color: "#00e676",
  },
  {
    range: "10k - 25k",
    service: "Grátis",
    marketing: "R$ 0,375",
    utility: "R$ 0,038",
    authentication: "R$ 0,038",
    color: "#a2f2a1",
  },
  {
    range: "25k - 50k",
    service: "Grátis",
    marketing: "R$ 0,375",
    utility: "R$ 0,036",
    authentication: "R$ 0,036",
    color: "#00e676",
  },
  {
    range: "+ de 50k",
    service: "Grátis",
    marketing: "R$ 0,375",
    utility: "R$ 0,034",
    authentication: "R$ 0,034",
    color: "#a2f2a1",
  },
];

const PriceCard = ({
  children,
  bgcolor = "#fff",
  color = "#455a64",
  minWidth = "120px",
  compact = false,
  isHighlighted = false,
}) => (
  <Box
    sx={{
      bgcolor: isHighlighted ? "#e3f2fd" : bgcolor,
      color: isHighlighted ? "#4e189aff" : color,
      borderRadius: "8px",
      height: compact ? { xs: "38px", md: "42px" } : { xs: "45px", md: "55px" },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: 1,
      minWidth: compact ? "80px" : minWidth,
      boxShadow: isHighlighted
        ? "0px 0px 0px 2px #4e189aff"
        : bgcolor === "#fff"
          ? "0px 2px 4px rgba(0,0,0,0.05)"
          : "none",
      border: isHighlighted
        ? "none"
        : bgcolor === "#fff"
          ? "1px solid #f0f2f5"
          : "none",
      transform: isHighlighted ? "scale(1.03)" : "scale(1)",
      transition: "all 0.2s ease",
      zIndex: isHighlighted ? 1 : 0,
    }}
  >
    <Typography
      sx={{
        fontSize: compact
          ? { xs: "0.6rem", md: "0.75rem" }
          : { xs: "0.65rem", md: "0.85rem" },
        fontWeight: isHighlighted ? 900 : 700,
      }}
    >
      {children}
    </Typography>
  </Box>
);

export default function CostPricingPopover({
  anchorEl,
  onClose,
  channelType,
  category,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isWhatsAppAPI = channelType === "whatsapp_api";
  const packageInfo = CHANNEL_PACKAGES[channelType];

  const headers = [
    { label: "Volume/Mês", key: "volume" },
    { label: "Serviço", key: "SERVICE" },
    { label: "Marketing", key: "MARKETING" },
    { label: "Utilidade", key: "UTILITY" },
    { label: "Autenticação", key: "AUTH" },
  ];

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      PaperProps={{
        sx: {
          p: 2.5,
          borderRadius: "12px",
          maxWidth: "95vw",
          width: isWhatsAppAPI ? "auto" : { xs: "280px", md: "350px" },
          bgcolor: "#ffffff",
          overflowX: "auto",
          boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
          border: "1px solid rgba(0,0,0,0.08)",
        },
      }}
    >
      {isWhatsAppAPI ? (
        <Box sx={{ minWidth: isMobile ? "600px" : "650px" }}>
          {/* HEADER */}
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            {headers.map((h) => (
              <Typography
                key={h.key}
                sx={{
                  flex: 1,
                  minWidth: "120px",
                  textAlign: "center",
                  fontSize: "0.7rem",
                  fontWeight: category === h.key ? 900 : 700,
                  color: category === h.key ? "#4e189aff" : "#546e7a",
                  textTransform: "uppercase",
                  borderBottom:
                    category === h.key
                      ? "2px solid #4e189aff"
                      : "2px solid transparent",
                  pb: 0.5,
                  transition: "all 0.2s ease",
                }}
              >
                {h.label}
              </Typography>
            ))}
          </Stack>

          {/* ROWS */}
          {WHATSAPP_PRICING.map((row, i) => (
            <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }}>
              <PriceCard bgcolor={row.color} color="#003300">
                {row.range}
              </PriceCard>
              <PriceCard isHighlighted={category === "service"}>
                {row.service}
              </PriceCard>
              <PriceCard isHighlighted={category === "marketing"}>
                {row.marketing}
              </PriceCard>
              <PriceCard isHighlighted={category === "utility"}>
                {row.utility}
              </PriceCard>
              <PriceCard isHighlighted={category === "authentication"}>
                {row.authentication}
              </PriceCard>
            </Stack>
          ))}
        </Box>
      ) : (
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              fontWeight: 800,
              color: "#546e7a",
              textAlign: "center",
            }}
          >
            Tabela de custos: {packageInfo?.label || "CANAL"}
          </Typography>

          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.5,
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                PACOTE
              </Typography>
              <PriceCard bgcolor="#e3f2fd" compact>
                {packageInfo?.unit || "Consultar"}
              </PriceCard>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.5,
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                VALOR
              </Typography>
              <PriceCard bgcolor="#00e676" color="#003300" compact>
                {packageInfo?.price || "—"}
              </PriceCard>
            </Box>
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          mt: 2,
          bgcolor: "#f8f9fa",
          borderRadius: "8px",
          py: 1.2,
          px: 2,
          border: "1px dashed #cfd8dc",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: "#546e7a",
            fontWeight: 700,
            lineHeight: 1.4,
          }}
        >
          {isWhatsAppAPI ? (
            <>
              * Valores unitários estimados com base no câmbio de R$ 6,00.
              Sujeitos à incidência de impostos.
              <br />
              <b>Modelo de cobrança:</b> por mensagem entregue. <b>Volume:</b>{" "}
              descontos progressivos.
            </>
          ) : (
            <>* {packageInfo?.info}</>
          )}
        </Typography>
      </Box>
    </Popover>
  );
}
